import React, { useState, useEffect } from 'react';
import { useUser } from '../context/UserContext';
import { getDetalleDevolucionesManuales } from '../api/apiConfig';
import { MdReceipt } from 'react-icons/md';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { IoSearchOutline, IoClose } from 'react-icons/io5';
import * as XLSX from 'xlsx';

const DetalleDevolucionesManuales = () => {
  const { user: userData } = useUser();
  const [devolucionesData, setDevolucionesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const itemsPerPage = 30;

  useEffect(() => {
    if (userData) {
      fetchDevolucionesData();
    }
  }, [userData]);

  const fetchDevolucionesData = async () => {
    try {
      setLoading(true);
      const response = await getDetalleDevolucionesManuales(userData);
      
      console.log('Response received:', response);
      
      // Manejar diferentes formatos de respuesta
      let data = response;
      
      // Si la respuesta tiene statusCode y body
      if (response && response.statusCode === 200 && response.body) {
        try {
          data = typeof response.body === 'string' ? JSON.parse(response.body) : response.body;
        } catch (e) {
          console.error('Error parsing body:', e);
          data = response.body;
        }
      }
      
      console.log('Processed data:', data);
      
      if (Array.isArray(data) && data.length > 0) {
        setDevolucionesData(data);
        setError(null);
      } else if (Array.isArray(data) && data.length === 0) {
        setDevolucionesData([]);
        setError('No hay registros de devoluciones manuales');
      } else {
        setError('Formato de datos no válido');
      }
    } catch (err) {
      setError(`Error al cargar los datos: ${err.message}`);
      console.error('Error completo:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(value);
  };

  const formatDate = (dateString) => {
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('es-CL');
  };

  const filterData = (dataToFilter) => {
    if (!searchTerm.trim()) return dataToFilter;
    const searchLower = searchTerm.toLowerCase().trim();
    return dataToFilter.filter(item => {
      const searchableFields = [item.FECHA, item.RUT, item.CLIENTE, item.EJECUTIVO, item.GLOSA, item.CARGOS, item.ABONOS];
      return searchableFields.some(field => field != null && String(field).toLowerCase().includes(searchLower));
    });
  };

  const getSortedData = () => {
    let dataToSort = filterData(devolucionesData);
    if (!sortColumn) return dataToSort;
    return [...dataToSort].sort((a, b) => {
      let aValue = a[sortColumn]; let bValue = b[sortColumn];
      if (aValue == null) aValue = ''; if (bValue == null) bValue = '';
      const aNum = typeof aValue === 'number' ? aValue : parseFloat(aValue);
      const bNum = typeof bValue === 'number' ? bValue : parseFloat(bValue);
      const isNumeric = !isNaN(aNum) && !isNaN(bNum) && isFinite(aNum) && isFinite(bNum);
      const comparison = isNumeric ? aNum - bNum : String(aValue).toLowerCase().localeCompare(String(bValue).toLowerCase(), 'es', { numeric: true, sensitivity: 'base' });
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  const sortedData = getSortedData();
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = sortedData.slice(startIndex, endIndex);

  const handleSort = (column) => {
    if (sortColumn === column) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    else { setSortColumn(column); setSortDirection('asc'); }
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => { setSearchTerm(e.target.value); setCurrentPage(1); };
  const clearSearch = () => { setSearchTerm(''); setCurrentPage(1); };

  const getSortIcon = (column) => {
    if (sortColumn !== column) return <FaSort style={{ marginLeft: '4px', opacity: 0.3 }} />;
    return sortDirection === 'asc' ? <FaSortUp style={{ marginLeft: '4px' }} /> : <FaSortDown style={{ marginLeft: '4px' }} />;
  };

  const exportToExcel = () => {
    const dataToExport = getSortedData();
    const exportData = dataToExport.map(item => ({
      'Fecha': formatDate(item.FECHA),
      'RUT': item.RUT,
      'Cliente': item.CLIENTE,
      'Ejecutivo': item.EJECUTIVO,
      'Glosa': item.GLOSA,
      'Cargos': item.CARGOS,
      'Abonos': item.ABONOS
    }));
    
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Detalle Devoluciones Manuales');
    
    XLSX.writeFile(wb, `detalle_devoluciones_manuales_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const goToPage = (page) => {
    setCurrentPage(page);
  };

  if (loading) {
    return (
      <div className="panel-container">
        <div className="flex justify-center items-center h-64">
          <div className="text-lg">Cargando datos...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="panel-container">
        <div className="panel-header">
          <h1 className="panel-title">Detalle de Devoluciones Manuales</h1>
        </div>
        <div className="flex justify-center items-center h-64">
          <div className="text-red-600 text-center">
            <div className="mb-2">{error}</div>
            <button 
              onClick={fetchDevolucionesData}
              className="btn-primary mt-2"
            >
              Reintentar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="panel-container">
      <div className="flex items-baseline gap-4 mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Detalle de Devoluciones Manuales</h1>
      </div>

      <div className="seccion-datos">
        <div className="flex justify-between items-center mb-4">
          <h3 style={{color: 'var(--if-primary)', margin: '0', fontSize: '1.2rem', fontWeight: '600'}}>
            Devoluciones Manuales
            <span className="text-sm ml-2" style={{color: 'var(--if-text-muted)'}}>
              ({devolucionesData.length} registros)
            </span>
          </h3>
          {devolucionesData.length > 0 && (
            <button
              onClick={exportToExcel}
              className="btn-primary"
              style={{
                background: 'linear-gradient(135deg, var(--if-success) 0%, #16a34a 100%)',
                padding: '0.5rem 1rem',
                fontSize: '0.875rem',
                borderLeft: '3px solid var(--if-primary)'
              }}
            >
              <MdReceipt className="w-4 h-4 mr-2" style={{display: 'inline'}} />
              Exportar Excel
            </button>
          )}
        </div>
        {devolucionesData.length > 0 && (
          <div className="mb-4">
            <div className="relative" style={{ maxWidth: '500px' }}>
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <IoSearchOutline className="h-5 w-5" style={{ color: '#9ca3af' }} />
              </div>
              <input type="text" value={searchTerm} onChange={handleSearchChange} placeholder="Buscar en todas las columnas..." className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              {searchTerm && <button type="button" onClick={clearSearch} className="absolute inset-y-0 right-0 pr-3 flex items-center" style={{ color: '#9ca3af' }} title="Limpiar búsqueda"><IoClose className="h-5 w-5" /></button>}
            </div>
            {searchTerm && <div className="mt-2 text-sm" style={{ color: 'var(--if-text-muted)' }}>{sortedData.length === 0 ? <span>No se encontraron resultados para &quot;{searchTerm}&quot;</span> : <span>{sortedData.length} resultado{sortedData.length !== 1 ? 's' : ''} encontrado{sortedData.length !== devolucionesData.length ? ` de ${devolucionesData.length} registros` : ''}</span>}</div>}
          </div>
        )}
        
        {sortedData.length === 0 && searchTerm ? (
          <div className="text-center py-8" style={{color: 'var(--if-text-muted)'}}>No se encontraron resultados para &quot;{searchTerm}&quot;</div>
        ) : (
        <div className="tabla-container">
          <table className="w-full" style={{fontSize: '0.875rem', tableLayout: 'fixed', width: '100%'}}>
            <thead>
              <tr>
                <th style={{width: '100px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('FECHA')}>Fecha {getSortIcon('FECHA')}</th>
                <th style={{width: '120px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('RUT')}>RUT {getSortIcon('RUT')}</th>
                <th style={{width: '250px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('CLIENTE')}>Cliente {getSortIcon('CLIENTE')}</th>
                <th style={{width: '180px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('EJECUTIVO')}>Ejecutivo {getSortIcon('EJECUTIVO')}</th>
                <th style={{width: '300px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('GLOSA')}>Glosa {getSortIcon('GLOSA')}</th>
                <th style={{width: '120px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('CARGOS')}>Cargos {getSortIcon('CARGOS')}</th>
                <th style={{width: '120px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('ABONOS')}>Abonos {getSortIcon('ABONOS')}</th>
              </tr>
            </thead>
            <tbody>
              {currentData.map((item, index) => (
                <tr key={index}>
                  <td>{formatDate(item.FECHA)}</td>
                  <td>{item.RUT}</td>
                  <td>{item.CLIENTE}</td>
                  <td>{item.EJECUTIVO}</td>
                  <td>{item.GLOSA}</td>
                  <td style={{textAlign: 'right'}}>{formatCurrency(item.CARGOS)}</td>
                  <td style={{textAlign: 'right'}}>{formatCurrency(item.ABONOS)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
        
        {sortedData.length > itemsPerPage && (
          <div className="flex justify-between items-center mt-4">
            <div className="text-sm" style={{color: 'var(--if-text-muted)'}}>
              Mostrando {startIndex + 1} a {Math.min(endIndex, sortedData.length)} de {sortedData.length} registros
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed"
                style={{borderColor: 'var(--if-gray-300)', color: 'var(--if-text-primary)'}}
              >
                Anterior
              </button>
              <span className="px-3 py-1 text-sm">
                Página {currentPage} de {totalPages}
              </span>
              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed"
                style={{borderColor: 'var(--if-gray-300)', color: 'var(--if-text-primary)'}}
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DetalleDevolucionesManuales;